import React, { useEffect, useRef } from 'react';
import { Compass, Users, Settings } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const WhatWeDoSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const points = [
    { icon: Compass, ...t.whatWeDo.point1 },
    { icon: Users, ...t.whatWeDo.point2 },
    { icon: Settings, ...t.whatWeDo.point3 },
  ];

  return (
    <section 
      id="what-we-do" 
      ref={sectionRef}
      className="py-32 md:py-40 bg-black relative"
      data-testid="what-we-do-section"
    >
      <div className="absolute inset-0 dot-pattern opacity-20" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Section Header */}
        <div className="text-center mb-20 md:mb-24 animate-on-scroll">
          <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-zinc-500 mb-4">
            {t.whatWeDo.overline}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-white mb-6">
            {t.whatWeDo.title}
          </h2>
          <p className="text-zinc-400 text-base md:text-lg max-w-3xl mx-auto font-light">
            {t.whatWeDo.subtitle}
          </p>
          <div className="w-16 h-px bg-white/20 mx-auto mt-8" />
        </div>

        {/* Points Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {points.map((point, index) => (
            <div 
              key={index}
              className="animate-on-scroll text-center"
              style={{ transitionDelay: `${index * 0.15}s` }}
            >
              <div className="card-premium p-10 md:p-12 min-h-[280px] group">
                {/* Icon */}
                <div className="w-16 h-16 mx-auto flex items-center justify-center border border-white/10 mb-8 group-hover:border-white/30 transition-colors duration-500">
                  <point.icon className="w-7 h-7 text-white/50 group-hover:text-white transition-colors duration-500" />
                </div>

                {/* Content */}
                <h3 className="font-sans text-sm tracking-[0.1em] uppercase font-medium text-white mb-4">
                  {point.title}
                </h3>
                <p className="text-zinc-500 text-sm leading-relaxed font-light">
                  {point.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
